import api from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

/** The assistant's API: settings, conversations and proposed changes. */
export const assistantAPI = {
  status: async (companyId) => (await api.get('/assistant/status', { params: { companyId } })).data,
  setEnabled: async (companyId, enabled) => (await api.put(`/assistant/settings?companyId=${companyId}`, { enabled })).data,
  glossary: async (companyId) => (await api.get('/assistant/glossary', { params: { companyId } })).data,
  addGlossary: async (companyId, entry) => (await api.post(`/assistant/glossary?companyId=${companyId}`, entry)).data,
  updateGlossary: async (companyId, id, entry) => (await api.put(`/assistant/glossary/${id}?companyId=${companyId}`, entry)).data,
  approveGlossary: async (companyId, id) => (await api.post(`/assistant/glossary/${id}/approve?companyId=${companyId}`)).data,
  removeGlossary: async (companyId, id) => (await api.delete(`/assistant/glossary/${id}`, { params: { companyId } })).data,
  setModels: async (companyId, settings) => (await api.put(`/assistant/models?companyId=${companyId}`, settings)).data,
  setCap: async (companyId, monthlyCapUsd) => (await api.put(`/assistant/cap?companyId=${companyId}`, { monthlyCapUsd: monthlyCapUsd === null ? null : Number(monthlyCapUsd) })).data,
  usage: async (companyId, month = null) => (await api.get('/assistant/usage', { params: { companyId, ...(month ? { month } : {}) } })).data,
  datasets: async (companyId) => (await api.get('/assistant/datasets', { params: { companyId } })).data,
  setDatasetAccess: async (companyId, dataset, access) => (await api.put(`/assistant/datasets/${encodeURIComponent(dataset)}?companyId=${companyId}`, { access })).data,
  conversations: async (companyId) => (await api.get('/assistant/conversations', { params: { companyId } })).data,
  start: async (companyId, pageContext = {}) => (await api.post(`/assistant/conversations?companyId=${companyId}`, { pageContext })).data,
  get: async (companyId, id) => (await api.get(`/assistant/conversations/${id}`, { params: { companyId } })).data,
  removeAll: async (companyId) => (await api.delete('/assistant/conversations', { params: { companyId } })).data,
  remove: async (companyId, id) => (await api.delete(`/assistant/conversations/${id}`, { params: { companyId } })).data,
  confirm: async (companyId, actionId) => (await api.post(`/assistant/actions/${actionId}/confirm?companyId=${companyId}`)).data,
  cancel: async (companyId, actionId) => (await api.post(`/assistant/actions/${actionId}/cancel?companyId=${companyId}`)).data,
};

/**
 * Sends a message and reads the streamed reply. Calls onEvent(event, data)
 * for each server-sent event (progress, text, message, result, question,
 * action, done, error). Resolves when the stream ends. model and
 * reasoningEffort are the person's choice, from those the company allows.
 */
export async function sendMessage(companyId, conversationId, text, onEvent, signal, { model = null, reasoningEffort = null } = {}) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/assistant/conversations/${conversationId}/messages?companyId=${companyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ text, ...(model ? { model } : {}), ...(reasoningEffort ? { reasoningEffort } : {}) }),
    signal,
  });
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || body.message || `The assistant could not be reached (${res.status}).`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let end;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const event = /^event: (.+)$/m.exec(block)?.[1];
      const data = /^data: (.*)$/m.exec(block)?.[1];
      if (!event) continue;
      let parsed = null;
      try {
        parsed = data ? JSON.parse(data) : null;
      } catch {
        parsed = null;
      }
      onEvent(event, parsed);
    }
  }
}
